import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/App';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';
import HospitalsTab from '@/components/admin/HospitalsTab';
import RegionsTab from '@/components/admin/RegionsTab';
import VendorsTab from '@/components/admin/VendorsTab';
import ItemsTab from '@/components/admin/ItemsTab';
import UsersTab from '@/components/admin/UsersTab';

export default function AdminDashboard({ user, onLogout, onUserUpdate }) {
  const [activeTab, setActiveTab] = useState('hospitals');
  const { t, setLanguage } = useLanguage();

  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    if (onUserUpdate) {
      onUserUpdate({ ...user, language: newLang });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Medorder - Demo</h1>
                <p className="text-sm text-gray-600">{user.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <LanguageSelector onLanguageChange={handleLanguageChange} />
              <Button
                onClick={onLogout}
                variant="outline"
                className="hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors"
                data-testid="logout-button"
              >
                {t('auth.logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-8 bg-white shadow-sm border border-gray-200 p-1 rounded-xl" data-testid="admin-tabs">
            <TabsTrigger value="hospitals" className="rounded-lg" data-testid="hospitals-tab">{t('nav.hospitals')}</TabsTrigger>
            <TabsTrigger value="regions" className="rounded-lg" data-testid="regions-tab">{t('nav.regions')}</TabsTrigger>
            <TabsTrigger value="vendors" className="rounded-lg" data-testid="vendors-tab">{t('nav.vendors')}</TabsTrigger>
            <TabsTrigger value="items" className="rounded-lg" data-testid="items-tab">{t('nav.items')}</TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg" data-testid="users-tab">{t('nav.users')}</TabsTrigger>
          </TabsList>

          <TabsContent value="hospitals">
            <HospitalsTab />
          </TabsContent>

          <TabsContent value="regions">
            <RegionsTab />
          </TabsContent>

          <TabsContent value="vendors">
            <VendorsTab />
          </TabsContent>

          <TabsContent value="items">
            <ItemsTab />
          </TabsContent>

          <TabsContent value="users">
            <UsersTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}